import { useMemo } from 'react';
import { formatUnits } from 'viem';
import { useAccount, useReadContracts } from 'wagmi';
import { MARKET_PAIRS } from '../data/mockMarket';
import { PAIR_ABI } from './abis';
import { TARGET_CHAIN } from './config';
import { getDeployment } from './contracts';

const toNum = (v: bigint) => Number(formatUnits(v, 18));

export interface Prices {
  ready: boolean;
  /** USDT-denominated spot price for a token symbol (USDT = 1). */
  priceOf: (symbol: string) => number | undefined;
  /** USD value of 1 LP token of the given pool id. */
  lpPriceOf: (poolId: string) => number | undefined;
  /** total reserves value (both sides) in USD for a pool id. */
  poolTvlOf: (poolId: string) => number | undefined;
}

// Shared price oracle for the whole app's earn/farm/lending screens: one
// multicall reads every AMM pool's reserves + token0 + LP supply, from which
// token spot prices, LP token prices, and pool TVLs are derived.
export function useTokenPrices(): Prices {
  const { chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);

  const contracts = useMemo(
    () =>
      MARKET_PAIRS.flatMap((p) => {
        const addr = deployment?.pairs[p.id];
        return [
          { address: addr, abi: PAIR_ABI, functionName: 'getReserves', chainId: readChainId } as const,
          { address: addr, abi: PAIR_ABI, functionName: 'token0', chainId: readChainId } as const,
          { address: addr, abi: PAIR_ABI, functionName: 'totalSupply', chainId: readChainId } as const,
        ];
      }),
    [deployment, readChainId],
  );

  const { data } = useReadContracts({
    contracts,
    query: { enabled: Boolean(deployment), refetchInterval: 10000 },
  });

  return useMemo(() => {
    const price = new Map<string, number>([['USDT', 1]]);
    const pool = new Map<string, { rBase: number; rQuote: number; supply: number; base: string; quote: string }>();

    if (!data || !deployment) {
      return {
        ready: false,
        priceOf: () => undefined,
        lpPriceOf: () => undefined,
        poolTvlOf: () => undefined,
      };
    }

    MARKET_PAIRS.forEach((p, i) => {
      const res = data[i * 3];
      const t0 = data[i * 3 + 1];
      const ts = data[i * 3 + 2];
      if (res.status !== 'success' || t0.status !== 'success' || ts.status !== 'success') return;
      const [r0, r1] = res.result as readonly [bigint, bigint, number];
      const baseAddr = deployment.tokens[p.base]?.toLowerCase();
      const baseIs0 = (t0.result as string).toLowerCase() === baseAddr;
      const rBase = toNum(baseIs0 ? r0 : r1);
      const rQuote = toNum(baseIs0 ? r1 : r0);
      pool.set(p.id, { rBase, rQuote, supply: toNum(ts.result as bigint), base: p.base, quote: p.quote });
      if (p.quote === 'USDT' && rBase > 0) price.set(p.base, rQuote / rBase);
      if (p.base === 'USDT' && rQuote > 0) price.set(p.quote, rBase / rQuote);
    });

    const priceOf = (symbol: string) => price.get(symbol);

    const poolTvlOf = (poolId: string): number | undefined => {
      const p = pool.get(poolId);
      if (!p) return undefined;
      const pb = price.get(p.base);
      const pq = price.get(p.quote);
      if (pb === undefined && pq === undefined) return undefined;
      const baseVal = pb !== undefined ? p.rBase * pb : p.rQuote * (pq ?? 0);
      const quoteVal = pq !== undefined ? p.rQuote * pq : p.rBase * (pb ?? 0);
      return baseVal + quoteVal;
    };

    const lpPriceOf = (poolId: string): number | undefined => {
      const p = pool.get(poolId);
      const tvl = poolTvlOf(poolId);
      if (!p || tvl === undefined || p.supply <= 0) return undefined;
      return tvl / p.supply;
    };

    return { ready: true, priceOf, lpPriceOf, poolTvlOf };
  }, [data, deployment]);
}
